import type { StepProps } from '../types';
import { ToggleField } from '../fields';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ALL_STATES } from '@/tax/states';
import type { FilingStatus, TaxScenarioInput } from '@/tax/types';
import Import1040Dialog from '../Import1040Dialog';

interface FilingStepProps extends StepProps {
  onImport1040?: (fields: Partial<TaxScenarioInput>) => void;
  /** `import_wage_split` flag: the import screen's W-2 wage split box. */
  wageSplitBox?: boolean;
  /** `roth_plan` flag: birth-year boxes, which the multi-year Roth plan reads for RMD and Medicare ages. */
  birthYears?: boolean;
}

/** A typed year, or undefined when the box is blank or not yet four digits. */
function parseBirthYear(raw: string): number | undefined {
  const n = Number(raw);
  return raw.length === 4 && Number.isInteger(n) && n >= 1900 && n <= 2100 ? n : undefined;
}

export default function FilingStep({ input, onChange, onImport1040, wageSplitBox, birthYears }: FilingStepProps) {
  return (
    <div className="space-y-8">
      {onImport1040 && (
        <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3" data-tour="import-1040">
          <div className="text-sm">
            <p className="font-medium">Have a prior-year 1040?</p>
            <p className="text-xs text-muted-foreground">Pre-fill the wizard from a tax-software-generated PDF.</p>
          </div>
          <Import1040Dialog onApply={onImport1040} wageSplitBox={wageSplitBox} />
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-sm font-semibold">Client Name</Label>
        <Input value={input.name} onChange={e => onChange('name', e.target.value)} className="h-11 text-base" placeholder="My Client" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-tour="filing-basics">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">Filing Status</Label>
          <Select value={input.filingStatus} onValueChange={v => onChange('filingStatus', v as FilingStatus)}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="S">Single</SelectItem>
              <SelectItem value="MFJ">Married Filing Jointly</SelectItem>
              <SelectItem value="MFS">Married Filing Separately</SelectItem>
              <SelectItem value="HOH">Head of Household</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">State</Label>
          <Select value={input.state} onValueChange={v => onChange('state', v)}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ALL_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div data-tour="filing-age">
        <h3 className="text-sm font-semibold mb-4">Age-Related Elections</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ToggleField label="Taxpayer 65+" value={input.tp65Plus} onChange={v => { onChange('tp65Plus', v); if (v) onChange('tp55Plus', true); }} />
          <ToggleField label="Spouse 65+ (MFJ)" value={input.sp65Plus} onChange={v => { onChange('sp65Plus', v); if (v) onChange('sp55Plus', true); }} />
          <ToggleField label="Taxpayer 55+" value={input.tp55Plus} onChange={v => onChange('tp55Plus', v)} disabled={input.tp65Plus} />
          <ToggleField label="Spouse 55+ (MFJ)" value={input.sp55Plus} onChange={v => onChange('sp55Plus', v)} disabled={input.sp65Plus} />
          <ToggleField label="NY TP 59½+" value={input.nyTp59Half} onChange={v => onChange('nyTp59Half', v)} />
          <ToggleField label="NY SP 59½+" value={input.nySp59Half} onChange={v => onChange('nySp59Half', v)} />
        </div>
        {birthYears && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="filing-birth-years">
            <div className="space-y-2">
              <Label htmlFor="tpBirthYear" className="text-xs font-medium text-muted-foreground">Taxpayer birth year</Label>
              <Input
                id="tpBirthYear"
                type="number"
                inputMode="numeric"
                value={input.tpBirthYear ?? ''}
                onChange={e => onChange('tpBirthYear', parseBirthYear(e.target.value))}
                className="h-10 text-sm font-mono"
                placeholder="1960"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="spBirthYear" className="text-xs font-medium text-muted-foreground">Spouse birth year (MFJ)</Label>
              <Input
                id="spBirthYear"
                type="number"
                inputMode="numeric"
                value={input.spBirthYear ?? ''}
                onChange={e => onChange('spBirthYear', parseBirthYear(e.target.value))}
                className="h-10 text-sm font-mono"
                placeholder="1962"
              />
            </div>
            <p className="text-xs text-muted-foreground italic md:col-span-2 lg:col-span-3">
              Used by the multi-year Roth plan for when required distributions start and when Medicare begins.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
